import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Avatar,
  Button,
} from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { apiClient } from '../../services/api';
import { PageHeader, SectionCard, StatTile, EmptyState, LoadingState, StatusPill } from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatDate, formatNumber, initials } from '../../utils/format';

interface AdminSalon {
  id: string;
  name: string;
  city: string;
  isActive: boolean;
  owner: { id: string; email: string; firstName?: string; lastName?: string };
  _count: { services: number; staff: number; bookings: number };
  createdAt: string;
}

const numCell = { fontVariantNumeric: 'tabular-nums' } as const;

export const SuperAdminSalonsPage: React.FC = () => {
  const navigate = useNavigate();
  const [salons, setSalons] = useState<AdminSalon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/salons', { params: { limit: 100 } });
      setSalons(res.data.data || []);
    } catch {
      setError('Nie udało się załadować listy salonów. Odśwież stronę za chwilę.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const active = salons.filter((s) => s.isActive).length;
  const inactive = salons.length - active;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Administracja' }]}
        title="Salony w systemie"
        description={loading
          ? 'Wszystkie salony zarejestrowane na platformie Visitio.'
          : `${countLabel(salons.length, 'salon', 'salony', 'salonów')} na platformie · ${countLabel(active, 'aktywny', 'aktywne', 'aktywnych')}`}
        actions={<Button variant="outlined" onClick={() => navigate('/admin/stats')}>Statystyki platformy</Button>}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'grid', gap: { xs: 1.25, sm: 2 }, mb: 2.5, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        <StatTile label="Salony" value={formatNumber(salons.length)} loading={loading} />
        <StatTile
          label="Aktywne"
          value={formatNumber(active)}
          loading={loading}
          progress={salons.length ? (active / salons.length) * 100 : undefined}
        />
        <StatTile label="Nieaktywne" value={formatNumber(inactive)} loading={loading} />
      </Box>

      {loading ? (
        <LoadingState variant="table" rows={4} />
      ) : salons.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Na platformie nie ma jeszcze salonów"
            description="Salon pojawi się tutaj, gdy właściciel założy konto i doda swój salon."
            actions={<Button variant="outlined" onClick={() => navigate('/salons/new')}>Dodaj salon</Button>}
          />
        </SectionCard>
      ) : (
        <SectionCard title="Lista salonów" subtitle="Kliknij salon, żeby zobaczyć usługi, zespół i godziny otwarcia." padding="none">
          <TableContainer>
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Salon</TableCell>
                  <TableCell>Miasto</TableCell>
                  <TableCell>Właściciel</TableCell>
                  <TableCell align="right">Usługi</TableCell>
                  <TableCell align="right">Zespół</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Utworzono</TableCell>
                  <TableCell padding="checkbox" aria-label="Otwórz" />
                </TableRow>
              </TableHead>
              <TableBody>
                {salons.map((salon) => {
                  const parts = salon.name.trim().split(/\s+/);
                  return (
                    <TableRow
                      key={salon.id}
                      hover
                      onClick={() => navigate(`/salons/${salon.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.25}>
                          <Avatar
                            variant="rounded"
                            sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: tokens.plumSoft, color: tokens.plum, fontSize: '0.8125rem' }}
                          >
                            {initials(parts[0], parts[1], salon.name)}
                          </Avatar>
                          <Typography sx={{ fontWeight: 600, fontSize: 'inherit', whiteSpace: 'nowrap' }}>{salon.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{salon.city}</TableCell>
                      <TableCell sx={{ color: salon.owner?.email ? tokens.ink : tokens.subtle }}>{salon.owner?.email || '—'}</TableCell>
                      <TableCell align="right" sx={numCell}>{formatNumber(salon._count?.services || 0)}</TableCell>
                      <TableCell align="right" sx={numCell}>{formatNumber(salon._count?.staff || 0)}</TableCell>
                      <TableCell>
                        <StatusPill label={salon.isActive ? 'Aktywny' : 'Nieaktywny'} tone={salon.isActive ? 'success' : 'neutral'} />
                      </TableCell>
                      <TableCell sx={{ ...numCell, whiteSpace: 'nowrap' }}>{salon.createdAt ? formatDate(salon.createdAt) : '—'}</TableCell>
                      <TableCell padding="checkbox" sx={{ color: tokens.subtle }}>
                        <ChevronRightRoundedIcon sx={{ display: 'block' }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}
    </Box>
  );
};
